import { Service } from '@toeverything/infra';

import type { OfflineMutationRecord } from '../../offline-mutation-outbox/types';
import { ViewBackedAppApiClientService } from './api-client';
import {
  sourceOperation,
  supportsSourceOperation,
  ViewBackedAppMutationOutboxService,
} from './mutation-outbox';
import { viewBackedAppResourceId } from './resource-id';
import type {
  ViewBackedAppDescriptor,
  ViewBackedAppSource,
  ViewBackedAppSourceAttachInput,
  ViewBackedAppSourceCandidate,
  ViewBackedAppSourcePreferencePatch,
  ViewBackedAppSourceSyncState,
} from './types';
import { viewBackedAppSourceRefKey } from './types';
import { decodeViewBackedAppSourcesResponseV1 } from './wire-contract';

export interface ViewBackedAppWorkspaceLoadResult<T> {
  data: T | undefined;
  error: unknown | null;
}

interface SourceMutationOverlay {
  mutation: OfflineMutationRecord;
  syncState: ViewBackedAppSourceSyncState;
}

export class ViewBackedAppService extends Service {
  constructor(
    private readonly api: ViewBackedAppApiClientService,
    private readonly mutationOutbox: ViewBackedAppMutationOutboxService
  ) {
    super();
  }

  async listWorkspaceSources<TSource extends ViewBackedAppSource>(input: {
    decodeSource: (source: unknown) => TSource;
    descriptor: ViewBackedAppDescriptor;
    listLocalSources: (serverSources: readonly TSource[]) => Promise<TSource[]>;
    workspaceId: string;
  }): Promise<ViewBackedAppWorkspaceLoadResult<{ sources: TSource[] }>> {
    let serverSources: TSource[] = [];
    let syncError: unknown = null;
    try {
      const response = decodeViewBackedAppSourcesResponseV1(
        await this.api.listSources(input.descriptor, input.workspaceId)
      );
      serverSources = response.sources.map(input.decodeSource);
    } catch (error) {
      syncError = error;
    }

    try {
      const localSources = await input.listLocalSources(serverSources);
      return {
        data: {
          sources: this.applyPendingSourceMutations(
            input.descriptor,
            mergeSources(serverSources, localSources)
          ),
        },
        error: syncError,
      };
    } catch (localError) {
      return {
        data: serverSources.length
          ? {
              sources: this.applyPendingSourceMutations(
                input.descriptor,
                serverSources
              ),
            }
          : undefined,
        error: serverSources.length ? syncError : localError,
      };
    }
  }

  async listWorkspaceCandidates<
    TCandidate extends ViewBackedAppSourceCandidate,
  >(input: {
    descriptor: ViewBackedAppDescriptor;
    listLocalCandidates: () => Promise<TCandidate[]>;
    sources: readonly ViewBackedAppSource[];
    workspaceId: string;
  }): Promise<ViewBackedAppWorkspaceLoadResult<{ candidates: TCandidate[] }>> {
    let serverCandidates: TCandidate[] = [];
    let syncError: unknown = null;
    try {
      serverCandidates = (
        await this.api.listSourceCandidates(input.descriptor, input.workspaceId)
      ).candidates as TCandidate[];
    } catch (error) {
      syncError = error;
    }

    try {
      const localCandidates = await input.listLocalCandidates();
      return {
        data: {
          candidates: mergeCandidates(
            serverCandidates,
            localCandidates,
            input.sources
          ),
        },
        error: syncError,
      };
    } catch (localError) {
      return {
        data: serverCandidates.length
          ? {
              candidates: mergeCandidates(serverCandidates, [], input.sources),
            }
          : undefined,
        error: serverCandidates.length ? syncError : localError,
      };
    }
  }

  attachSource(
    descriptor: ViewBackedAppDescriptor,
    input: ViewBackedAppSourceAttachInput
  ): Promise<unknown> {
    return this.mutationOutbox.enqueueAttachSource(descriptor, input);
  }

  createSource(
    descriptor: ViewBackedAppDescriptor,
    input: ViewBackedAppSourceAttachInput
  ): Promise<unknown> {
    return this.mutationOutbox.enqueueCreateSource(descriptor, input);
  }

  prepareSourceForServerMutation(
    descriptor: ViewBackedAppDescriptor,
    target: Pick<ViewBackedAppSourceAttachInput, 'databaseId' | 'docId'>
  ): Promise<void> {
    return this.mutationOutbox.prepareSourceForServerMutation(
      descriptor,
      target
    );
  }

  updateSourcePreference(
    descriptor: ViewBackedAppDescriptor,
    sourceId: string,
    patch: ViewBackedAppSourcePreferencePatch
  ): Promise<unknown> {
    return this.mutationOutbox.enqueueUpdateSourcePreference(
      descriptor,
      sourceId,
      patch
    );
  }

  removeSource(
    descriptor: ViewBackedAppDescriptor,
    workspaceId: string,
    sourceId: string
  ): Promise<{ removed: boolean }> {
    return this.api.removeSource(descriptor, workspaceId, sourceId);
  }

  applyPendingSourceMutations<TSource extends ViewBackedAppSource>(
    descriptor: ViewBackedAppDescriptor,
    sources: readonly TSource[]
  ): TSource[] {
    const sourcesById = new Map(sources.map(source => [source.id, source]));
    for (const { mutation, syncState } of this.sourceMutationOverlays(
      descriptor
    )) {
      const isCreateSource =
        supportsSourceOperation(descriptor, 'createSource') &&
        mutation.operation === sourceOperation(descriptor, 'createSource');
      const isAttachSource =
        supportsSourceOperation(descriptor, 'attachSource') &&
        mutation.operation === sourceOperation(descriptor, 'attachSource');
      if (isCreateSource || isAttachSource) {
        const payload = mutation.payload as ViewBackedAppSourceAttachInput;
        const sourceId = viewBackedAppResourceId(
          descriptor,
          payload.docId,
          payload.databaseId,
          payload.viewId
        );
        const source = sourcesById.get(sourceId);
        if (source) {
          sourcesById.set(sourceId, {
            ...source,
            syncState,
            title: payload.title?.trim() || source.title,
          });
        }
        continue;
      }

      if (
        !supportsSourceOperation(descriptor, 'updateSourcePreference') ||
        mutation.operation !==
          sourceOperation(descriptor, 'updateSourcePreference')
      ) {
        continue;
      }
      const payload = mutation.payload as {
        patch: ViewBackedAppSourcePreferencePatch;
        sourceId: string;
      };
      const source = sourcesById.get(payload.sourceId);
      if (!source) continue;
      sourcesById.set(payload.sourceId, {
        ...source,
        color:
          payload.patch.colorOverride === undefined
            ? source.color
            : payload.patch.colorOverride,
        syncState,
        title: payload.patch.titleOverride ?? source.title,
        visible: payload.patch.visible ?? source.visible,
      });
    }
    return [...sourcesById.values()].sort(compareSources) as TSource[];
  }

  private sourceMutationOverlays(
    descriptor: ViewBackedAppDescriptor
  ): SourceMutationOverlay[] {
    return [
      ...this.mutationOutbox.pendingMutations$.value
        .filter(mutation =>
          mutation.operation.startsWith(`${descriptor.appKind}.`)
        )
        .map(mutation => ({
          mutation,
          syncState: 'pending' as const,
        })),
      ...this.mutationOutbox.failedMutations$.value
        .filter(mutation =>
          mutation.operation.startsWith(`${descriptor.appKind}.`)
        )
        .map(mutation => ({
          mutation,
          syncState: 'failed' as const,
        })),
    ].sort((left, right) => left.mutation.createdAt - right.mutation.createdAt);
  }
}

function mergeSources<TSource extends ViewBackedAppSource>(
  serverSources: readonly TSource[],
  localSources: readonly TSource[]
): TSource[] {
  const sourcesByRef = new Map<string, TSource>();
  for (const source of serverSources) {
    sourcesByRef.set(viewBackedAppSourceRefKey(source), source);
  }
  for (const source of localSources) {
    const existing = sourcesByRef.get(viewBackedAppSourceRefKey(source));
    sourcesByRef.set(viewBackedAppSourceRefKey(source), {
      ...existing,
      ...source,
      color: source.color ?? existing?.color ?? null,
      visible: existing?.visible ?? source.visible,
    } as TSource);
  }
  return [...sourcesByRef.values()].sort(compareSources);
}

function mergeCandidates<TCandidate extends ViewBackedAppSourceCandidate>(
  serverCandidates: readonly TCandidate[],
  localCandidates: readonly TCandidate[],
  sources: readonly ViewBackedAppSource[]
): TCandidate[] {
  const attachedRefs = new Set(sources.map(viewBackedAppSourceRefKey));
  const candidatesByRef = new Map<string, TCandidate>();
  for (const candidate of serverCandidates) {
    if (!attachedRefs.has(viewBackedAppSourceRefKey(candidate))) {
      candidatesByRef.set(viewBackedAppSourceRefKey(candidate), candidate);
    }
  }
  for (const candidate of localCandidates) {
    if (!attachedRefs.has(viewBackedAppSourceRefKey(candidate))) {
      candidatesByRef.set(viewBackedAppSourceRefKey(candidate), candidate);
    }
  }
  return [...candidatesByRef.values()].sort(compareCandidates);
}

function compareSources(
  a: ViewBackedAppSource,
  b: ViewBackedAppSource
): number {
  return (
    (a.area?.name ?? '').localeCompare(b.area?.name ?? '') ||
    a.title.localeCompare(b.title) ||
    a.id.localeCompare(b.id)
  );
}

function compareCandidates(
  left: ViewBackedAppSourceCandidate,
  right: ViewBackedAppSourceCandidate
): number {
  return (
    (left.area?.name ?? '').localeCompare(right.area?.name ?? '') ||
    left.title.localeCompare(right.title) ||
    viewBackedAppSourceRefKey(left).localeCompare(
      viewBackedAppSourceRefKey(right)
    )
  );
}
